import { KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { SidebarNavItem } from './sidebarNavigation';

type SidebarGroupFlyoutProps = {
  item: SidebarNavItem;
  anchor: HTMLElement;
  /** Right edge of the sidebar, so the flyout never covers it. */
  left: number;
  id: string;
  focusFirst: boolean;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onClose: (restoreFocus: boolean) => void;
  onNavigate: (to: string) => void;
};

/**
 * Sub-pages of a sidebar group. Opens on hover or with ArrowRight, closes on
 * Escape, ArrowLeft, focus leaving it, or navigation (handled by the caller).
 */
export const SidebarGroupFlyout = ({
  item,
  anchor,
  left,
  id,
  focusFirst,
  onPointerEnter,
  onPointerLeave,
  onClose,
  onNavigate,
}: SidebarGroupFlyoutProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(() => anchor.getBoundingClientRect().top);

  useLayoutEffect(() => {
    const panel = ref.current;
    if (!panel) return;
    const anchorTop = anchor.getBoundingClientRect().top;
    const maxTop = window.innerHeight - panel.offsetHeight - 8;
    setTop(Math.max(8, Math.min(anchorTop, maxTop)));
    if (focusFirst) {
      panel.querySelector<HTMLElement>('a')?.focus();
    }
  }, [anchor, focusFirst]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const links = Array.from(
      ref.current?.querySelectorAll<HTMLElement>('a') ?? [],
    );
    const index = links.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'Escape' || event.key === 'ArrowLeft') {
      event.preventDefault();
      onClose(true);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      links[(index + 1) % links.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      links[(index - 1 + links.length) % links.length]?.focus();
    }
  };

  return createPortal(
    <div
      ref={ref}
      id={id}
      className="make-sidebar-flyout"
      role="menu"
      tabIndex={-1}
      aria-label={item.title}
      style={{ left, top }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onKeyDown={onKeyDown}
      onBlur={event => {
        const next = event.relatedTarget as Node | null;
        if (!next || (!ref.current?.contains(next) && !anchor.contains(next))) {
          onClose(false);
        }
      }}
    >
      <Link
        className="make-sidebar-flyout__heading"
        to={item.path}
        role="menuitem"
        tabIndex={-1}
        onClick={() => onNavigate(item.path)}
      >
        {item.title}
      </Link>
      {item.submenu?.map((section, index) => (
        <div
          key={section.label ?? index}
          className="make-sidebar-flyout__section"
          role="group"
          aria-label={section.label}
        >
          {section.label && (
            <div className="make-sidebar-flyout__label" aria-hidden="true">
              {section.label}
            </div>
          )}
          {section.items.map(link => (
            <Link
              key={link.to}
              className="make-sidebar-flyout__link"
              to={link.to}
              role="menuitem"
              tabIndex={-1}
              onClick={() => onNavigate(link.to)}
            >
              {link.title}
            </Link>
          ))}
        </div>
      ))}
    </div>,
    document.body,
  );
};
